import i18n from "src/i18n"
import { flagFor } from "src/assets/flags"
import { activeWorld, wrapColumn } from "src/utils/world"

const getMaximumPathFromNodes = (nodes) => {
  /* From [{col: 12, row: 13}, {...}] returns the longest feasable path for troops
  that respect ortogonal movement */

  const returnSequence = (path) => {
    if (path.length == 1) return null
    return path
  }

  const feasablePath = []
  nodes.forEach((node) => {
    if (feasablePath.length == 0) {
      feasablePath.push(node)
    } else {
      const lastNode = feasablePath[feasablePath.length - 1]
      if (node.row === lastNode.row || node.col === lastNode.col) {
        feasablePath.push(node)
      } else {
        return returnSequence(feasablePath)
      }
    }
  })
  return returnSequence(feasablePath)
}

/* The world wraps east to west (2026-09-05): the shortest way from column 198 to column 1 is
   through 199 and 0, and every route here takes it. `shortDelta` is the signed column
   difference the short way round; the server measures the same way, so a path built here is a
   path it accepts. */
const shortDelta = (from, to) => {
  const COLUMNS = activeWorld().columns
  let delta = to - from
  if (delta > COLUMNS / 2) delta -= COLUMNS
  if (delta < -COLUMNS / 2) delta += COLUMNS
  return delta
}

/* One orthogonal step apart, measured the short way round the cylinder like everything else
   here — so 199 → 0 is a step and 199 → 1 is not. Rows do not wrap. */
const isNeighbour = (from, to) => {
  if (!Array.isArray(from) || !Array.isArray(to)) return false
  return Math.abs(shortDelta(from[0], to[0])) + Math.abs(to[1] - from[1]) === 1
}

const cameFromOf = (movement) => {
  const walked = Array.isArray(movement.walked) ? movement.walked : []
  const cell = movement.walked_entered_from || walked[0] || movement.came_from
  return Array.isArray(cell) ? { col: cell[0], row: cell[1] } : null
}

/* Every cell a step from the next: a road a stack could have walked. */
const isRoad = (cells) => cells.every((cell, i) => i === 0 || isNeighbour(cells[i - 1], cell))

const expandNodesToPath = (nodes) => {
  /* Turn the waypoints the player clicked into the cell-by-cell path the server expects.

  `selectedNodes` holds corners, not cells, and it stores the origin *last* — `startMovementFromCity`
  seeds it and `resetMovementNodes` keeps the final element as the survivor. Reverse it so the
  origin comes first, then walk each segment one cell at a time.

  Segments that are not axis-aligned are walked column-first then row, producing an L. The server
  rejects anything non-orthogonal, so it is better to emit a legal path than a rejected one. */

  if (!nodes || nodes.length < 2) return []

  // Coerce: the canvas derives col/row from the "col-row" cell keys and hands them over as
  // STRINGS. Left as-is, `current.column !== target.column` compares 53 to "53", never converges,
  // and the while-loop below spins forever and freezes the tab. Found by playing, not by tests.
  const waypoints = [...nodes].reverse().map((node) => ({ column: Number(node.col), row: Number(node.row) }))
  const path = [{ column: waypoints[0].column, row: waypoints[0].row }]

  const stepTowards = (target) => {
    if (!Number.isFinite(target.column) || !Number.isFinite(target.row)) return
    let current = path[path.length - 1]
    while (current.column !== target.column) {
      // The short way round the world, one wrapped step at a time.
      current = { column: wrapColumn(current.column + Math.sign(shortDelta(current.column, target.column))), row: current.row }
      path.push(current)
    }
    while (current.row !== target.row) {
      current = { column: current.column, row: current.row + Math.sign(target.row - current.row) }
      path.push(current)
    }
  }

  waypoints.slice(1).forEach((node) => stepTowards(node))
  return simplifyPath(path)
}

/* Drop the stutters from a path before anyone reads it (owner's Q25): a cell repeated back to
   back is a stutter a careless waypoint produces. A there-and-back is *not* dropped any more
   (owner's R7, 2026-09-03: "there are some cases where you want to go back") — the server
   accepts a route that revisits a cell, or ends where it began, so the client draws and sends
   exactly what the player traced. */
const simplifyPath = (path) => {
  const walked = []
  for (const cell of path) {
    const top = walked[walked.length - 1]
    if (top && top.column === cell.column && top.row === cell.row) continue
    walked.push(cell)
  }
  return walked
}

/* The simplest route between two cells that a click did not line up (owner's R15): breadth-first
   over land, four neighbours, so the path is the shortest orthogonal walk that keeps its feet
   dry; when no dry route exists the search is repeated over water too, because a route is
   better than a refusal. The answer is the list of *corners* (waypoints), origin excluded, in
   walking order — what `selectedNodes` stores and `expandNodesToPath` walks straight between.
   Null when the two are the same cell or nothing at all connects them. */
const findWaypoints = (from, to) => {
  const start = { column: Number(from.col ?? from.column), row: Number(from.row) }
  const goal = { column: Number(to.col ?? to.column), row: Number(to.row) }
  if (start.column === goal.column && start.row === goal.row) return null
  const rows = activeWorld().rows
  // Only a row can leave the map; a column wraps (and the old `<=` let row 100 in — there is
  // no row 100).
  const inside = (r) => r >= 0 && r < rows
  const search = (walkable) => {
    const key = (c, r) => `${c}-${r}`
    const cameFrom = new Map([[key(start.column, start.row), null]])
    const queue = [start]
    while (queue.length) {
      const current = queue.shift()
      if (current.column === goal.column && current.row === goal.row) break
      for (const [dc, dr] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ]) {
        const c = wrapColumn(current.column + dc)
        const r = current.row + dr
        if (!inside(r) || cameFrom.has(key(c, r))) continue
        if (!walkable({ column: c, row: r }) && !(c === goal.column && r === goal.row)) continue
        cameFrom.set(key(c, r), current)
        queue.push({ column: c, row: r })
      }
    }
    if (!cameFrom.has(key(goal.column, goal.row))) return null
    const cells = []
    let step = goal
    while (step) {
      cells.unshift(step)
      step = cameFrom.get(key(step.column, step.row))
    }
    return cells
  }
  const cells = search((cell) => !isWater(cell)) || search(() => true)
  if (!cells) return null
  return cornersOf(cells.map(({ column, row }) => [column, row]))
    .slice(1)
    .map(([column, row]) => ({ col: column, row }))
}

/* What a cell is *called*, which is a different question from which country owns it.
 *
 * It used to be one answer to both, and a player's three starting cities were all called "Russia":
 * the Cities list, its filter box, every march order and every battle report named a place the
 * player held three of. So `name` is now the real town on that cell — `cityNames.json`, built by
 * `scripts/build-city-names.py` — and `country` is kept beside it for the screens that want the
 * larger place, which is the city sheet's subtitle and nothing else so far.
 *
 * `name` falls back to the country, because 1,936 land cells have no town to be named after and
 * 1,664 of those are Antarctica. A generated name beside a real flag reads worse than an honest
 * one; the fallback is exactly what every cell showed before.
 *
 * `flag` is an image the bundle carries, not a URL somebody else serves — see assets/flags. It can
 * be undefined (a territory with no published flag), which is why every caller guards the `<img>`
 * rather than rendering a broken one.
 */
const getPlace = ({ key, row, column }) => {
  const cellKey = key ? key : `${column}-${row}`
  const world = activeWorld()
  const countryCode = world.matrix[cellKey]
  const country = world.countries[countryCode]
  const flag = flagFor(world.countryFlags[countryCode])
  return { name: world.cityNames[cellKey] || country, country, flag }
}

/* A cell the world map has no country for still has to be called something: troops can stand on
   unclaimed ground, and a list row whose name is `undefined` took the whole Cities tab down with
   `name.toLowerCase()`. The sea selects since 2026-09-08 (a march pauses on it while it saves
   the points to cross), and a sea cell is the sea — never a camp, held or not. */
const cellDisplayName = ({ key, row, column, held = false }) => {
  const { name } = getPlace({ key, row, column })
  if (name) return name
  if (!activeWorld().matrix[key ? key : `${column}-${row}`]) return i18n.t("map.openSea")
  return held ? i18n.t("map.outpost") : i18n.t("map.unclaimedCamp")
}

const isWater = ({ column, row }) => {
  const cellKey = `${column}-${row}`
  const countryCode = activeWorld().matrix[cellKey]
  return !countryCode
}

/* Live movements as the canvas draws them: one arrow per stack from where it is now to where it
   is going. The canvas joins consecutive nodes border-to-border, so the nodes are the corners of
   the remaining path (a run of straight cells would otherwise be a chain of zero-length legs).
   `relation` is "self" | "ally" | "enemy"; an ally also carries its team, because a partner's
   colour depends on which side they are — blue for humans, orange for the horde (2026-08-21). */
const cornersOf = (cells) => {
  if (cells.length <= 2) return cells
  const corners = [cells[0]]
  for (let i = 1; i < cells.length - 1; i++) {
    const [pc, pr] = cells[i - 1]
    const [c, r] = cells[i]
    const [nc, nr] = cells[i + 1]
    // Column steps are read the short way round, so a straight walk across the seam is not a
    // corner: 198 → 199 → 0 is one line, not a hairpin.
    const turns = shortDelta(pc, c) !== shortDelta(c, nc) || r - pr !== nr - r
    if (turns) corners.push(cells[i])
  }
  corners.push(cells[cells.length - 1])
  return corners
}

/* A movement is the road ahead (owner, 2026-09-07): the server cuts a moved stack's path back to
   the cell it stands on at the end of every round, so there is no trail to draw and nobody —
   the owner included — reads where a march was raised. A stack that has arrived has nothing
   left to draw; its bodies show as a stack on the cell. */
const movementsForCanvas = (movements, { userId, teamOf }) =>
  (movements || []).flatMap((movement) => {
    const path = movement.path || []
    if (!path.length) return []
    const index = Math.max(0, Math.min(movement.current_index ?? 0, path.length - 1))
    const at = path[index]
    let remaining = path.slice(index)
    // A stack held in a fight keeps one cell of history (owner, 2026-09-07): the arrow is drawn
    // from the cell it set out from into the fight, so the defender sees which way it came.
    // Only when that cell is a neighbour of where the stack stands. This is two cells drawn as
    // one straight segment, so it is a legal step or it is a lie: game 2 stored a `came_from`
    // several cells back (fixed server-side in `forget_the_road_behind`), and round 18 drew
    // 102-17 → 103-14 across ground the stack had walked but the arrow skipped, round 24 drew
    // the diagonal 104-12 → 103-13. Snapshots written before that fix still hold those cells,
    // and a replay of one must show no arrow rather than a road that never existed.
    if (remaining.length < 2 && isNeighbour(movement.came_from, at)) remaining = [movement.came_from, at]
    // And the whole road it walked in that round (owner, 2026-10-08, game 137: history is the
    // rounds before, not this one). Drawn only when every cell is a step from the next.
    // Where it came out of the viewer's fog on the way (`walked_entered_from`), the line starts
    // on that fog square and fades in, as an order out of the fog does.
    const walked = Array.isArray(movement.walked) ? movement.walked : []
    const enteredFrom = movement.walked_entered_from
    const walkedRoad = Array.isArray(enteredFrom) ? [enteredFrom, ...walked, at] : [...walked, at]
    const walkedOutOfFog = remaining.length <= 2 && walkedRoad.length > 1 && isRoad(walkedRoad) && Array.isArray(enteredFrom)
    if (remaining.length <= 2 && walkedRoad.length > 1 && isRoad(walkedRoad)) remaining = walkedRoad
    // Ordered out of the fog (owner, 2026-10-05): the road starts on the fog square it stepped out
    // of, and the map fades it in from there.
    const orderedOutOfFog = Boolean(movement.from_out_of_sight) && isNeighbour(movement.entered_from, path[0])
    if (orderedOutOfFog) remaining = [movement.entered_from, ...path]
    const fromOutOfSight = orderedOutOfFog || walkedOutOfFog
    if (remaining.length < 2) return []
    const isSelf = movement.user_id === userId
    const relation = isSelf ? "self" : teamOf(movement.user_id) === teamOf(userId) ? "ally" : "enemy"
    return [
      {
        id: movement.id,
        type: relation === "ally" ? `ally_${teamOf(movement.user_id)}` : relation,
        action: "attack",
        // The arrow, from where the stack stands to where it is going.
        nodes: cornersOf(remaining).map(([column, row]) => `${column}-${row}`),
        currentPosition: `${at[0]}-${at[1]}`,
        // A past round's enemy road cut where the viewer's sight ended (`fog.js`, the server's
        // `_cut_to_sight`): its last square is not where it went, so the map fades it out.
        outOfSight: Boolean(movement.out_of_sight),
        // And one that came out of the fog: the line fades in, and nothing is framed where the
        // stack never stood.
        fromOutOfSight,
        // A stack in a fight whose road came out of the fog does stand where the line ends, so
        // that cell is framed; an order out of the fog never stood on its road.
        standsAtEnd: walkedOutOfFog,
      },
    ]
  })

/* The movements a cell is part of — standing on it (and so leaving from it next), heading for
   it, or crossing it further on — in the shape the hover tooltip and the city modal read. The
   road behind a stack is gone from the payload (owner, 2026-09-07), so a movement's first cell
   is where it stands: a cell is "from" when the stack is on it and going on, "to" when it is the
   destination, "route" when it lies ahead. Composition is whatever the server sent — it blanks
   an enemy's under fog and discloses it for a stack the viewer is fighting — never re-hidden
   here. */
const movementsTouching = (movements, cell, { userId, teamOf }) => {
  const col = Number(cell.col)
  const row = Number(cell.row)
  const same = ([c, r]) => c === col && r === row
  return (movements || []).flatMap((movement) => {
    const path = movement.path || []
    if (!path.length) return []
    const destination = path[path.length - 1]
    const index = Math.min(movement.current_index ?? 0, path.length - 1)
    const position = movement.position || path[index]
    // Whether the stack stands on this very cell right now — fighting at its gates, stopped by
    // its garrison, or resting on it — as opposed to heading for it (owner's R26: a city's troop
    // list must count the stacks fighting there).
    // A road out of the fog (owner, 2026-10-05) never had its stack on its first square: that
    // square is on its route, not where it stood.
    const standing = !movement.from_out_of_sight && Boolean(position && same(position))
    const arrived = Boolean(movement.has_arrived) || index >= path.length - 1
    const roles = []
    if (same(destination)) roles.push("to")
    else if (standing) roles.push("from")
    // Any cell still ahead on the route counts too (owner's ask, 2026-08-25): hovering or opening
    // ground a march will cross should say so, not stay silent until the stack stands there.
    if (!roles.length && path.slice(index).some(same)) roles.push("route")
    if (!roles.length) return []
    const isSelf = movement.user_id === userId
    const relation = isSelf ? "self" : teamOf(movement.user_id) === teamOf(userId) ? "ally" : "enemy"
    const unitEntries = Object.entries(movement.units || {})
      .map(([type, data]) => ({ type, number: data?.number ?? 0 }))
      .filter((u) => u.number > 0)
    return [
      {
        id: movement.id,
        relation,
        roles,
        standing,
        arrived,
        // Where a stack in a fight set out from in the round it was halted (2026-09-07; the
        // round's start since 2026-10-08, `walked[0]`, else the last leg of an older snapshot).
        cameFrom: cameFromOf(movement),
        userId: movement.user_id,
        origin: { col: position[0], row: position[1] },
        destination: { col: destination[0], row: destination[1] },
        // Every stack still travelling has an ETA (owner's S14, 2026-09-05), the one-round order
        // included: a stack that has not arrived needs at least one more round whatever the
        // payload says, so a missing or zero `ticks_remaining` reads as one, never as nothing.
        roundsLeft: movement.has_arrived ? 0 : Math.max(1, movement.ticks_remaining ?? 1),
        halted: Boolean(movement.halted),
        // Every march the viewer sees comes with its units, an enemy's too (36/V1, 2026-10-26).
        totalUnits: unitEntries.reduce((total, u) => total + u.number, 0),
        units: unitEntries,
      },
    ]
  })
}

/* What a movement's line says about the force on the road: its composition, an enemy's as a
   player's own (owner, 2026-10-26, 36/V1: no more headcount). Only an enemy march that arrives
   with no units at all — an older payload — is "unknown". */
const forceDisclosure = (movement) => {
  if (movement.units?.length > 0) return "units"
  return movement.relation === "enemy" ? "unknown" : ""
}

export {
  getMaximumPathFromNodes,
  expandNodesToPath,
  findWaypoints,
  getPlace,
  cellDisplayName,
  isWater,
  movementsForCanvas,
  movementsTouching,
  forceDisclosure,
  cornersOf,
}
